import type { ReactElement } from 'react';

import type { ProjectMortgageRules } from '../hooks/useMortgageRules';
import MortgageLeadManagementCard from './MortgageLeadManagementCard';
import MortgagePreEvaluationCard from './MortgagePreEvaluationCard';

interface Props {
  project: { id: number; name: string; countryCode: string };
  subProjects: Array<{ id: number; name: string }>;
  byProject: Map<number, ProjectMortgageRules>;
}

interface ProjectSectionProps {
  projectId: number;
  projectName: string;
  countryCode: string;
  rules: ProjectMortgageRules;
}

/* What one project of a financial entity needs: the financing switch with its vertical, and the entities it
   pre-evaluates against. No agent prompts, agent modes or DS19: an entity answers with a single prompt. */
const ProjectSection = ({ projectId, projectName, countryCode, rules }: ProjectSectionProps): ReactElement => (
  <div className="grid gap-6 lg:grid-cols-2">
    <MortgageLeadManagementCard projectId={projectId} projectName={projectName} rules={rules.rules} />
    <MortgagePreEvaluationCard
      projectId={projectId}
      countryCode={countryCode}
      config={rules.config}
      configRow={rules.configRow}
      topology="leadManagement"
    />
  </div>
);

/*
 * A financial entity (Lead Management), with or without products. Its rules are read where the client is: on
 * the master while it has no product, and on the product (a sub-project) once it is assigned one. So the master
 * always gets its own section, and each product another with the same two cards.
 */
const MortgageFinancialEntityView = ({ project, subProjects, byProject }: Props): ReactElement => {
  const master = byProject.get(project.id);

  return (
    <div className="flex flex-col gap-6">
      {master && (
        <section className="flex flex-col gap-3">
          {subProjects.length > 0 && (
            <h3 className="m-0 text-base font-semibold text-text-default font-display">
              Entidad, antes de asignar un producto
            </h3>
          )}
          <ProjectSection
            projectId={project.id}
            projectName={project.name}
            countryCode={project.countryCode}
            rules={master}
          />
        </section>
      )}

      {subProjects.map((subProject) => {
        const rules = byProject.get(subProject.id);
        if (!rules) return null;
        return (
          <section key={subProject.id} className="flex flex-col gap-3">
            <h3 className="m-0 text-base font-semibold text-text-default font-display">Producto: {subProject.name}</h3>
            <ProjectSection
              projectId={subProject.id}
              projectName={subProject.name}
              countryCode={project.countryCode}
              rules={rules}
            />
          </section>
        );
      })}
    </div>
  );
};

export default MortgageFinancialEntityView;
